import React, { useEffect, useState } from "react";
import { BsFillMenuButtonWideFill } from "react-icons/bs";
import { IoInformationCircleOutline } from "react-icons/io5";
import { AiOutlinePlus, AiOutlineDelete } from "react-icons/ai";
import TemplateQuestions from "./TemplateQuestions";

import { FaUserLarge } from "react-icons/fa6";
import { toast } from "react-toastify";
import saveLogo from "./apis/saveLogo";
import saveChatUI from "./apis/saveChatUI";

const SuggestedMessagesInfo = ({ handleToggle, prev }) => {
  const [showInfo, setShowInfo] = useState(false);

  return (
    <div className="relative mb-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm">Suggested Messages</span>
          <button
            onClick={() => setShowInfo((prev) => !prev)}
            className="text-gray-600 hover:text-gray-900"
            title="Info">
            <IoInformationCircleOutline size={16} />
          </button>
        </div>

        {/* Toggle Button */}
        <button
          onClick={handleToggle}
          className={`px-3 py-1 text-xs rounded-md font-medium shadow-sm transition-colors duration-200 ${
            prev
              ? "bg-red-100 text-red-600 hover:bg-red-200"
              : "bg-green-100 text-green-600 hover:bg-green-200"
          }`}>
          {prev ? "Hide" : "Show"}
        </button>
      </div>

      {/* Info Tooltip */}
      {showInfo && (
        <div className="absolute top-8 left-0 bg-gray-100 text-gray-800 text-xs p-3 rounded shadow-md max-w-xs z-10 mt-1">
          Help guide users with suggested messages at the bottom of the chat.
          Keep them concise (under 40 characters) and limit to 4 suggestions for
          the best experience.
        </div>
      )}
    </div>
  );
};

const messages = [
  { sender: "bot", text: "Hi there! 👋 How can I assist you today?" },
  {
    sender: "user",
    text: "Can you tell me the address of your nearest branch in Mumbai?",
  },
  {
    sender: "bot",
    text: "Sure! Our Mumbai branch is located at:\n📍 3rd Floor, Skyline Business Park, Andheri East, Mumbai, Maharashtra 400059.\nLet me know if you need directions or working hours!",
  },
];

const ChatInterface = ({ selectedBot }) => {
  const [toggle, setToggle] = useState(false);
  const [templeQues, setTempleQues] = useState([]);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [prev, setPrev] = useState(false);

  useEffect(() => {
    if (selectedBot) {
      setTempleQues(selectedBot?.templeQues || []);
      setIconPreview(selectedBot?.ui?.BotLogoUrl || null);
    }
  }, [selectedBot]);

  const handleQuestionChange = (index, newValue) => {
    const updated = [...templeQues];
    updated[index].que = newValue;
    setTempleQues(updated);
  };

  const handleDeleteQuestion = (index) => {
    const updated = templeQues.filter((_, idx) => idx !== index);
    setTempleQues(updated);
  };

  const handleAddQuestion = () => {
    if (templeQues.length >= 4) return;
    setTempleQues([...templeQues, { que: "" }]);
  };

  const handleIconUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isValidType = ["image/jpeg", "image/png", "image/svg+xml"].includes(
      file.type
    );
    const isValidSize = file.size <= 1024 * 1024;

    if (!isValidType) {
      toast.error("Only JPG, PNG, and SVG files are allowed.");
      return;
    }

    if (!isValidSize) {
      toast.error("File size must be under 1MB.");
      return;
    }

    setIconFile(file);
    setIconPreview(URL.createObjectURL(file));
  };

  const handleToggle = () => {
    setPrev((pre) => !pre);
  };

  const handelSave = async () => {
    const fileupload = await saveLogo(selectedBot?.botId, iconFile);
    if (fileupload.error) {
      toast.error("unable to uplad file , try again later...");
    }
    const ui = selectedBot?.ui;

    if (!fileupload.error) {
      ui.BotLogoUrl = fileupload.data;
    }

    const editChatAppUi = await saveChatUI(selectedBot?.botId, ui, templeQues);
    if (editChatAppUi.error) {
      toast.error(editChatAppUi.msg);
      return;
    }
    toast.success(editChatAppUi.msg);
    setIconFile(null);
    setIconPreview(`${fileupload.data}?r=${Math.random()}`);
    return;
  };

  return (
    <div className="flex flex-col md:flex-row w-full gap-8 p-2 sm:p-4 md:p-6 min-h-full bg-white border border-gray-300 rounded-md shadow-md">
      {/* Sidebar */}
      <div className="flex flex-col w-full md:w-2/5 h-fit p-6 border rounded-lg shadow-md border-gray-200 bg-white text-gray-800">
        <SuggestedMessagesInfo handleToggle={handleToggle} prev={prev} />

        <div className="space-y-2">
          {templeQues.map((ele, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input
                value={ele.que}
                onChange={(e) => handleQuestionChange(idx, e.target.value)}
                className="px-3 py-1 w-full border rounded text-sm shadow-sm"
                placeholder={`Message ${idx + 1}`}
                maxLength={40}
              />
              <button onClick={() => handleDeleteQuestion(idx)}>
                <AiOutlineDelete
                  size={18}
                  className="text-red-500 hover:text-red-700"
                />
              </button>
            </div>
          ))}
        </div>

        {templeQues.length < 4 && (
          <button
            onClick={handleAddQuestion}
            className="flex items-center gap-2 mt-4 px-4 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 hover:bg-gray-200 shadow-sm w-fit">
            <AiOutlinePlus size={16} />
            Add Template
          </button>
        )}

        {/* Chat Icon Upload */}
        <div className="mt-6">
          <label className="block font-medium mb-2 text-sm">Chat Icon</label>
          <div className="flex items-center gap-4">
            {iconPreview ? (
              <img
                src={iconPreview}
                alt="Chat Icon Preview"
                className="w-12 h-12 object-contain border rounded-full shadow"
              />
            ) : (
              <div className="min-w-12 min-h-12 border rounded-full flex items-center justify-center text-gray-500 bg-gray-100 text-sm">
                N/A
              </div>
            )}
            <div className=" break-word w-full">
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.svg"
                onChange={handleIconUpload}
                className="text-sm"
              />
              <p className="text-xs  text-gray-500 mt-1">
                Supports JPG, PNG, and SVG files up to 1MB
              </p>
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <button
            onClick={handelSave}
            className="px-5 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm font-medium transition-all duration-150">
            Save
          </button>
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex flex-1 min-h-[80vh] relative">
        <div className="w-full flex flex-col rounded-lg shadow-lg bg-white text-gray-900 border border-gray-300">
          {/* Chat Header */}
          <div className="relative flex items-center justify-between p-4 border-b border-gray-200 bg-blue-600 text-white rounded-t-lg">
            <div className="text-lg font-semibold">Chat with us</div>
            <button
              onClick={() => setToggle((prev) => !prev)}
              className="hover:text-gray-200"
              title="Menu">
              <BsFillMenuButtonWideFill size={24} />
            </button>

            {/* Dropdown Menu */}
            {toggle && (
              <div className="absolute top-12 right-4 bg-white text-black rounded shadow-md p-2 text-sm z-10 border border-gray-200">
                <div className="py-1 hover:bg-gray-100 px-3 rounded cursor-pointer">
                  New Chat
                </div>
                <div className="py-1 hover:bg-gray-100 px-3 rounded cursor-pointer">
                  Download Transcript
                </div>
                <div className="py-1 hover:bg-gray-100 px-3 rounded text-red-500 cursor-pointer">
                  End Chat
                </div>
              </div>
            )}
          </div>

          {/* Chat Body */}
          <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
            {!prev && (
              <div className="mt-4 flex flex-col">
                {messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-2 ${
                      msg.sender === "bot" ? "self-start " : "self-start mt-6 "
                    }`}>
                    {/* Avatar/Icon */}
                    <div className="flex-shrink-0">
                      {msg.sender === "bot" ? (
                        <img
                          src={iconPreview}
                          alt="Bot Icon"
                          className="w-8 h-8 rounded-full border"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center text-xs font-semibold">
                          <FaUserLarge size={14} />
                        </div>
                      )}
                    </div>

                    {/* Message Bubble */}
                    <p>{msg.text}</p>
                  </div>
                ))}
              </div>
            )}

            {prev && (
              <TemplateQuestions
                questions={templeQues}
                handleTemplateSubmit={() => {}}
              />
            )}
          </div>

          {/* Input Box */}
          <div className="p-4 border-t border-gray-200 bg-white">
            <input
              type="text"
              className="w-full px-4 py-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Type your message..."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
